import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase, NEWS_TABLE } from '@/lib/supabase';
import type { NewsRecord } from '@/lib/types';
import { formatDate } from '@/lib/format';
import { useSeoOverride } from '@/seo/useSeoOverride';

export default function NewsDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState<NewsRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setItem(null);
    (async () => {
      try {
        const { data, error: err } = await supabase
          .from(NEWS_TABLE)
          .select('*')
          .eq('slug', slug)
          .eq('is_published', true)
          .maybeSingle();
        if (err) throw err;
        if (active) setItem(data);
      } catch {
        if (active) setError('記事の取得に失敗しました。');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  const paragraphs = item?.content ? item.content.split(/\n\n+/) : [];

  useSeoOverride(
    item
      ? {
          title: `${item.title}｜株式会社ルヴィラ`,
          description: (item.content || '').replace(/\s+/g, ' ').trim().slice(0, 120),
          article: {
            headline: item.title,
            description: (item.content || '').replace(/\s+/g, ' ').trim().slice(0, 160),
            datePublished: item.published_at || undefined,
          },
        }
      : null,
  );

  return (
    <article className="pt-32 md:pt-40 pb-20 md:pb-28 px-4 md:px-6 bg-[#0d0d1a]">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/news/"
          className="inline-flex items-center gap-2 text-sm text-[#a0a0b0] hover:text-[#a78bfa] transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          ニュース一覧へ戻る
        </Link>

        {loading ? (
          <div className="mt-12 bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-16 text-center text-[#a0a0b0]">
            <i className="ri-loader-4-line animate-spin text-2xl"></i>
          </div>
        ) : error ? (
          <div className="mt-12 bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-12 text-center">
            <p className="text-[#e8e8f0]">{error}</p>
          </div>
        ) : !item ? (
          <div className="mt-12 bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-16 text-center">
            <i className="ri-file-unknow-line text-4xl text-[#7a7a8a]"></i>
            <p className="mt-4 text-[#e8e8f0]">記事が見つかりませんでした。</p>
          </div>
        ) : (
          <>
            <div className="mt-8 flex items-center gap-3">
              <span className="px-3 py-1 rounded-full bg-[#7c3aed]/15 text-[#a78bfa] text-xs font-medium">
                {item.category}
              </span>
              <span className="text-xs text-[#7a7a8a]">{formatDate(item.published_at)}</span>
            </div>

            <h1 className="mt-4 text-2xl md:text-4xl font-extrabold text-white leading-tight">{item.title}</h1>

            <div className="mt-8 h-px bg-[#2a2a3e]"></div>

            <div className="mt-8 space-y-6">
              {paragraphs.map((p, i) => (
                <p key={i} className="text-[#e8e8f0] text-base leading-loose">
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-12">
              <Link
                to="/news/"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl h-12 px-8 text-sm font-medium border border-[#2a2a3e] bg-[#1a1a2e] text-white transition-all duration-300 hover:border-white/60 cursor-pointer"
              >
                一覧に戻る
              </Link>
            </div>
          </>
        )}
      </div>
    </article>
  );
}